import React, { useState, useEffect } from 'react';
import { useModel } from 'umi';
import {
  Card,
  Input,
  Button,
  Select,
  Table,
  Space,
  Tag,
  Avatar,
  DatePicker,
  Row,
  Col,
  Tabs,
  Tooltip,
  Badge,
  Image,
  Typography,
  Collapse,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  BarChartOutlined,
  StarOutlined,
  MessageOutlined,
  ShareAltOutlined,
  EyeOutlined,
  HeartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import './noteSearch.less';
import { getNoteStats, getNoteXsecTokenFromUser } from '@/services/xiaohongshu';
import categoriesData from '@/data/categories.json';
import type { Categories } from '@/types/categories';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Text } = Typography;

interface StatNoteItem {
  id: string;
  type: 'video' | 'normal';
  title: string;
  coverUrl: string;
  createTime: string;
  desc: string;
  likedCount: string;
  collectedCount: string;
  commentsCount: string;
  sharedCount: string;
  interactiveCount: string;
  predReadnum: string;
  user: {
    nickname: string;
    images: string;
    userid: string;
    userAttribute?: number; // 用户属性标识
  };
  tags: string[];
  officialKeyword: string[];
}

const userAttributes = [
  {
    label: '明星',
    value: 1,
    explain: '在小红书官方认证过演员、歌手、艺人等信息的账号',
  },
  {
    label: '品牌号',
    value: 2,
    explain: '在小红书上进行过商业投放行为的账号',
  },
  {
    label: '企业机构号',
    value: 3,
    explain: '除品牌号外，认证类型为企业和机构的账号',
  },
  {
    label: '头部KOL',
    value: 4,
    explain: '粉丝数>50w的账号',
  },
  {
    label: '腰部达人',
    value: 5,
    explain: '粉丝数5w—50w的账号',
  },
  {
    label: '初级达人',
    value: 6,
    explain: '粉丝数5k—5w的账号',
  },
  {
    label: '素人',
    value: 7,
    explain: '粉丝数0—5k的账号',
  },
];

// 从JSON文件导入分类数据
const categories: Categories = categoriesData as Categories;

const NoteSearch: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<StatNoteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedNoteType, setSelectedNoteType] = useState<string>(''); // 笔记类型
  const [selectedTime, setSelectedTime] = useState<'24h' | '7d' | '30d'>('24h'); // 发布时间
  const [commentFeature, setCommentFeature] = useState<string>(''); // 莫名其妙
  const [currentSortColumn, setCurrentSortColumn] =
    useState<string>('likedCount'); // 当前排序的列
  const [qrModalVisible, setQrModalVisible] = useState<boolean>(false); // 二维码弹窗显示状态
  const [selectedRecord, setSelectedRecord] = useState<StatNoteItem | null>(
    null,
  ); // 选中的记录
  const [searchKeyword, setSearchKeyword] = useState<string>(''); // 搜索框输入内容
  const [searchParams, setSearchParams] = useState<any>({
    keyword: '',
    noteType: '',
    createTime: '24h',
    sort: 'likedCount',
    authorGender: '',
    isMcn: '',
    fanRange: '',
    province: '',
    city: '',
  });

  // 获取笔记数据
  const fetchNoteData = async (
    params: any = {},
    page: number = currentPage,
    size: number = pageSize,
  ) => {
    try {
      setLoading(true);
      const searchData = {
        data: {
          input: {
            keyword: searchKeyword || '',
            type: ['title', 'content', 'tag'] as Array<
              | 'title'
              | 'topic'
              | 'content'
              | 'tag'
              | 'name'
              | 'categoryName'
              | 'seedBrandName'
            >,
          },
          baseInfoRequest: {
            createTime: selectedTime || '24h',
            noteType: '',
            firstType: selectedCategory,
            secondType: selectedSubCategory,
            type: '',
            contentTags: [],
            commentFeature: commentFeature || '',
          },
          high: {
            isMcn: params.isMcn || searchParams.isMcn || '',
            authorGender:
              params.authorGender || searchParams.authorGender || '',
            userAttribute: [],
            province: params.province || searchParams.province || '',
            city: params.city || searchParams.city || '',
            fan: {
              fixedRange: params.fanRange || searchParams.fanRange || '',
              customizeRange: '',
            },
            likedCollectedCountRange: { fixedRange: '', customizeRange: '' },
            interactiveCountRange: { fixedRange: '', customizeRange: '' },
            likedCountRange: { fixedRange: '', customizeRange: '' },
            collectedCountRange: { fixedRange: '', customizeRange: '' },
            commentsCountRange: { fixedRange: '', customizeRange: '' },
            sharedCountRange: { fixedRange: '', customizeRange: '' },
            predReadnumRange: { fixedRange: '', customizeRange: '' },
          },
          sort: params.sort || searchParams.sort || 'likedCount',
          size: size,
          start: page, // 从1开始计数
          custom: {
            must: [],
            should: [],
            mustNot: [],
          },
          source: {
            type: '',
            keyword: '',
          },
          time: selectedTime || '24h',
          startTime: '',
          endTime: '',
          filterIncomplete: 0,
        },
      };

      const { resp: response } = (await getNoteStats(searchData)) as any;

      if (response && response.data && Array.isArray(response.data.list)) {
        // 转换接口数据为组件需要的格式
        const formattedData = response.data.list.map((item: any) => ({
          id: item.id,
          type: item.type || 'normal', // 添加类型字段
          title: item.title || '未设置笔记标题',
          coverUrl: item.firstFrameUrl || 'https://picsum.photos/120/120',
          createTime: formatTime(item.createTime),
          desc: item.desc || '',
          likedCount: item.likedCount || '0',
          collectedCount: item.collectedCount || '0',
          commentsCount: item.commentsCount || '0',
          sharedCount: item.sharedCount || '0',
          interactiveCount: item.interactiveCount || '0',
          predReadnum: item.predReadnum || '0',
          user: {
            nickname: item.user?.nickname || '未知用户',
            images: item.user?.images || 'https://picsum.photos/32/32',
            userid: item.user?.userid || item.userid || '',
            userAttribute: item.user?.userAttribute,
          },
          tags: item.tags || [],
          officialKeyword: item.officialKeyword || [],
        }));

        setDataSource(formattedData);
        setTotal(response.data.count || formattedData.length);
      } else if (response && response.data && response.data.list) {
        // 备用的数据结构
        const formattedData = response.data.list.map((item: any) => ({
          id: item.id,
          type: item.type || 'normal', // 添加类型字段
          title: item.title || '未设置笔记标题',
          coverUrl: item.firstFrameUrl || 'https://picsum.photos/120/120',
          createTime: formatTime(item.createTime),
          desc: item.desc || '',
          likedCount: item.likedCount || '0',
          collectedCount: item.collectedCount || '0',
          commentsCount: item.commentsCount || '0',
          sharedCount: item.sharedCount || '0',
          interactiveCount: item.interactiveCount || '0',
          predReadnum: item.predReadnum || '0',
          user: {
            nickname: item.user?.nickname || '未知用户',
            images: item.user?.images || 'https://picsum.photos/32/32',
            userid: item.user?.userid || item.userid || '',
            userAttribute: item.user?.userAttribute,
          },
          tags: item.tags || [],
          officialKeyword: item.officialKeyword || [],
        }));
        setDataSource(formattedData);
        setTotal(response.data.total || formattedData.length);
      } else {
        setDataSource([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('获取笔记数据失败:', error);
      setDataSource([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 时间格式化函数
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (days > 0) {
      if (days === 1) return '1天前';
      if (days < 7) return `${days}天前`;
      if (days < 14) return '1周前';
      if (days < 30) return `${Math.floor(days / 7)}周前`;
      return `${Math.floor(days / 30)}个月前`;
    } else if (hours > 0) {
      return `${hours}小时前`;
    } else {
      return '刚刚';
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    // 设置全局的referrer policy以解决跨域图片访问问题
    const existingMeta = document.querySelector('meta[name="referrer"]');
    if (!existingMeta) {
      const meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = 'no-referrer';
      document.head.appendChild(meta);
    }

    // 同步searchParams中的keyword到searchKeyword状态
    if (searchParams.keyword) {
      setSearchKeyword(searchParams.keyword);
    }

    fetchNoteData();
  }, []);

  const handleSearch = (value?: string) => {
    // 使用传入的value或当前searchKeyword状态
    const keyword = value !== undefined ? value : searchKeyword;
    // 如果有传入的value，同步到searchKeyword状态
    if (value !== undefined) {
      setSearchKeyword(value);
    }
    // 重置到第一页并更新搜索参数
    setCurrentPage(1);
    const newParams = { ...searchParams, keyword };
    setSearchParams(newParams);
    fetchNoteData(newParams, 1, pageSize);
  };

  // 分页处理函数
  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    fetchNoteData(searchParams, page, size || pageSize);
  };

  // 处理一级分类选择
  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubCategory(''); // 清空二级分类选择
  };

  // 处理二级分类选择
  const handleSubCategoryChange = (value: string) => {
    setSelectedSubCategory(value);
  };

  // 处理笔记类型选择
  const handleNoteTypeChange = (value: string) => {
    setSelectedNoteType(value);
    if (value === 'normal') setCommentFeature('0');
    if (value === 'video') setCommentFeature('1');
  };

  // 处理发布时间选择
  const handleCreateTimeChange = (value: '24h' | '7d' | '30d') => {
    setSelectedTime(value);
  };

  // 获取当前选中分类的二级选项
  const getSubCategoryOptions = () => {
    const category = categories.find((cat) => cat.label === selectedCategory);
    return category ? category.children : [];
  };

  // 根据userAttribute值获取用户属性信息
  const getUserAttributeInfo = (userAttribute?: number) => {
    if (!userAttribute) return null;
    return userAttributes.find((attr) => attr.value === userAttribute);
  };

  // 带重试功能的图片组件
  const ImageWithRetry: React.FC<{
    src: string;
    width: number;
    height: number;
    style?: React.CSSProperties;
    className?: string;
    maxRetries?: number;
  }> = ({ src, width, height, style, className, maxRetries = 3 }) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [retryCount, setRetryCount] = useState(0);
    const [isError, setIsError] = useState(false);

    // 当src变化时重置状态
    useEffect(() => {
      setCurrentSrc(src);
      setRetryCount(0);
      setIsError(false);
    }, [src]);

    const handleError = () => {
      if (retryCount < maxRetries) {
        // 延迟重试，避免频繁请求
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          setCurrentSrc(`${src}?retry=${retryCount + 1}`);
        }, Math.pow(2, retryCount) * 1000); // 指数退避：1s, 2s, 4s
      } else {
        setIsError(true);
      }
    };

    if (isError) {
      // 显示默认占位图
      return (
        <div
          style={{
            width,
            height,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            color: '#999',
            fontSize: 12,
          }}
        >
          图片加载失败
        </div>
      );
    }

    return (
      <img
        src={currentSrc}
        width={width}
        height={height}
        className={className}
        style={style}
        onError={handleError}
        referrerPolicy="no-referrer"
        alt="笔记封面"
        loading="lazy"
      />
    );
  };

  const formatNumber = (num: string) => {
    if (num.includes('w')) {
      return num;
    }
    const numVal = parseInt(num);
    if (numVal >= 10000) {
      return `${(numVal / 10000).toFixed(1)}w`;
    }
    return num;
  };

  // 处理列标题点击排序
  const handleColumnSort = (columnKey: string) => {
    setCurrentSortColumn(columnKey);
    const newParams = { ...searchParams, sort: columnKey };
    setSearchParams(newParams);
    fetchNoteData(newParams, currentPage, pageSize);
  };

  // 处理详情按钮点击
  const handleShowDetails = (record: StatNoteItem) => {
    setSelectedRecord(record);
    setQrModalVisible(true);
  };

  // 关闭二维码弹窗
  const handleCloseQrModal = () => {
    setQrModalVisible(false);
    setSelectedRecord(null);
  };

  // 处理改写按钮点击
  const handleRewrite = async (record: StatNoteItem) => {
    try {
      setLoading(true);

      // 调用接口获取xsecToken
      const result = await getNoteXsecTokenFromUser({
        name: currentUser?.name || 'xiaohongshu',
        userId: record.user.userid,
        noteId: record.id,
      });

      if (result && result.resp && result.resp.code === 0) {
        const xsecToken = result.resp.data;

        // 构造URL
        const noteUrl = `https://xiaohongshu.com/explore/${record.id}?xsec_token=${xsecToken}&xsec_source=pc_user`;

        // 在新tab中打开note页面，并传递URL参数
        const notePageUrl = `/xiaohongshu/note?urlInput=${encodeURIComponent(
          noteUrl,
        )}`;
        window.open(notePageUrl, '_blank');
      } else {
        alert(`获取改写链接失败: ${result?.resp?.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('改写操作失败:', error);
      alert('改写操作失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const getColumns = (): ColumnsType<StatNoteItem> => [
    {
      title: '基本信息',
      key: 'basicInfo',
      width: 400,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <ImageWithRetry
              src={record.coverUrl}
              width={80}
              height={80}
              className="note-cover"
              style={{ borderRadius: 8, objectFit: 'cover' }}
            />
            {record.type === 'video' && (
              <div
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                视频
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="note-title"
              style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}
            >
              {record.title || '未设置笔记标题'}
            </div>
            <div style={{ marginBottom: 8 }}>
              {record.tags.slice(0, 4).map((tag, index) => (
                <Tag
                  key={index}
                  style={{ marginBottom: 4, fontSize: 14, fontWeight: 500 }}
                >
                  {tag}
                </Tag>
              ))}
            </div>
            <div className="user-info">
              <img
                src={record.user.images}
                referrerPolicy="no-referrer"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginRight: 8,
                }}
                alt="用户头像"
                onError={(e) => {
                  // 头像加载失败时使用默认头像
                  const target = e.target as HTMLImageElement;
                  target.src =
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMTAiIGZpbGw9IiNmNWY1ZjUiLz4KPHBhdGggZD0iTTEwIDEwQzExLjEwNDYgMTAgMTIgOS4xMDQ1NyAxMiA4QzEyIDYuODk1NDMgMTEuMTA0NiA2IDEwIDZDOC44OTU0MyA2IDggNi44OTU0MyA4IDhDOCA5LjEwNDU3IDguODk1NDMgMTAgMTAgMTBaIiBmaWxsPSIjY2NjIi8+CjxwYXRoIGQ9Ik02IDE2QzYgMTMuNzkwOSA3Ljc5MDg2IDEyIDEwIDEyQzEyLjIwOTEgMTIgMTQgMTMuNzkwOSAxNCAxNlY5SDZWMTZaIiBmaWxsPSIjY2NjIi8+Cjwvc3ZnPgo=';
                }}
              />
              <Text style={{ fontSize: 14, color: '#666', fontWeight: 500 }}>
                {record.user.nickname}
              </Text>
              {record.user.userAttribute &&
                getUserAttributeInfo(record.user.userAttribute) && (
                  <Tooltip
                    title={
                      getUserAttributeInfo(record.user.userAttribute)?.explain
                    }
                  >
                    <Badge
                      count={
                        getUserAttributeInfo(record.user.userAttribute)?.label
                      }
                      style={{ backgroundColor: '#1890ff', fontSize: 12 }}
                    />
                  </Tooltip>
                )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'createTime',
      width: 120,
      align: 'center',
      render: (time) => (
        <Text style={{ fontSize: 14, fontWeight: 500 }}>{time}</Text>
      ),
    },
    {
      title: (
        <Space
          style={{
            color:
              currentSortColumn === 'interactiveCount' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('interactiveCount')}
        >
          互动量
          <>↓</>
        </Space>
      ),
      key: 'interactiveCount',
      dataIndex: 'interactiveCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Text
          className="interaction-count"
          style={{
            fontWeight: 600,
            color:
              currentSortColumn === 'interactiveCount' ? '#1890ff' : '#000',
          }}
        >
          {formatNumber(count)}
        </Text>
      ),
    },
    {
      title: (
        <Space
          style={{
            color: currentSortColumn === 'predReadnum' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('predReadnum')}
        >
          预估阅读数
          <>↓</>
        </Space>
      ),
      key: 'predReadnum',
      dataIndex: 'predReadnum',
      width: 120,
      align: 'center',
      render: (count) => (
        <Text
          className="read-count"
          style={{
            fontWeight: 600,
            color: currentSortColumn === 'predReadnum' ? '#1890ff' : '#000',
          }}
        >
          {formatNumber(count)}
        </Text>
      ),
    },
    {
      title: (
        <Space
          style={{
            color: currentSortColumn === 'collectedCount' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('collectedCount')}
        >
          收藏
          <>↓</>
        </Space>
      ),
      key: 'collectedCount',
      dataIndex: 'collectedCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Space>
          <StarOutlined style={{ color: '#faad14' }} />
          <Text
            style={{
              fontSize: 14,
              fontWeight: 500,
              color:
                currentSortColumn === 'collectedCount' ? '#1890ff' : '#000',
            }}
          >
            {formatNumber(count)}
          </Text>
        </Space>
      ),
    },
    {
      title: (
        <Space
          style={{
            color: currentSortColumn === 'commentsCount' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('commentsCount')}
        >
          评论
          <>↓</>
        </Space>
      ),
      key: 'commentsCount',
      dataIndex: 'commentsCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Space>
          <MessageOutlined style={{ color: '#1890ff' }} />
          <Text
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: currentSortColumn === 'commentsCount' ? '#1890ff' : '#000',
            }}
          >
            {formatNumber(count)}
          </Text>
        </Space>
      ),
    },
    {
      title: (
        <Space
          style={{
            color: currentSortColumn === 'sharedCount' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('sharedCount')}
        >
          分享
          <>↓</>
        </Space>
      ),
      key: 'sharedCount',
      dataIndex: 'sharedCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Space>
          <ShareAltOutlined style={{ color: '#722ed1' }} />
          <Text
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: currentSortColumn === 'sharedCount' ? '#1890ff' : '#000',
            }}
          >
            {formatNumber(count)}
          </Text>
        </Space>
      ),
    },
    {
      title: (
        <Space
          style={{
            color: currentSortColumn === 'likedCount' ? '#1890ff' : '#000',
            cursor: 'pointer',
          }}
          onClick={() => handleColumnSort('likedCount')}
        >
          点赞
          <>↓</>
        </Space>
      ),
      key: 'likedCount',
      dataIndex: 'likedCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Space>
          <HeartOutlined style={{ color: '#ff4d4f' }} />
          <Text
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: currentSortColumn === 'likedCount' ? '#1890ff' : '#000',
            }}
          >
            {formatNumber(count)}
          </Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleShowDetails(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleRewrite(record)}
          >
            改写
          </Button>
        </Space>
      ),
    },
  ];

  const filterOptions = (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={3}>
          <Select
            placeholder="笔记分类"
            style={{ width: '100%' }}
            allowClear
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            {categories.map((category) => (
              <Option key={category.label} value={category.label}>
                {category.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={3}>
          <Select
            placeholder="二级分类"
            style={{ width: '100%' }}
            allowClear
            value={selectedSubCategory}
            onChange={handleSubCategoryChange}
            disabled={!selectedCategory}
          >
            {getSubCategoryOptions().map((subCategory) => (
              <Option key={subCategory.value} value={subCategory.value}>
                {subCategory.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Select
            placeholder="笔记类型"
            style={{ width: '100%' }}
            allowClear
            value={selectedNoteType}
            onChange={handleNoteTypeChange}
          >
            <Option value="normal">图文笔记</Option>
            <Option value="video">视频笔记</Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select
            placeholder="发布时间"
            style={{ width: '100%' }}
            allowClear
            value={selectedTime}
            onChange={handleCreateTimeChange}
          >
            <Option value="24h">24小时</Option>
            <Option value="7d">7天</Option>
            <Option value="30d">30天</Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select placeholder="打卡地" style={{ width: '100%' }} allowClear>
            <Option value="北京">北京</Option>
            <Option value="上海">上海</Option>
            <Option value="广州">广州</Option>
            <Option value="深圳">深圳</Option>
            <Option value="杭州">杭州</Option>
          </Select>
        </Col>
      </Row>
    </>
  );

  const advancedFilterOptions = (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col span={4}>
        <Select placeholder="性别" style={{ width: '100%' }} allowClear>
          <Option value="男">男</Option>
          <Option value="女">女</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="加入MCN" style={{ width: '100%' }} allowClear>
          <Option value="是">是</Option>
          <Option value="否">否</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="粉丝数" style={{ width: '100%' }} allowClear>
          <Option value="1000-5000">1000-5000</Option>
          <Option value="5000-1w">5000-1w</Option>
          <Option value="1w-10w">1w-10w</Option>
          <Option value="10w+">10w+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="账号属性" style={{ width: '100%' }} allowClear>
          <Option value="个人">个人</Option>
          <Option value="企业">企业</Option>
        </Select>
      </Col>
    </Row>
  );

  const dataOverviewOptions = (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col span={4}>
        <Select placeholder="互动量" style={{ width: '100%' }} allowClear>
          <Option value="1000+">1000+</Option>
          <Option value="5000+">5000+</Option>
          <Option value="1w+">1w+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="赞藏数" style={{ width: '100%' }} allowClear>
          <Option value="500+">500+</Option>
          <Option value="1000+">1000+</Option>
          <Option value="5000+">5000+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="点赞数" style={{ width: '100%' }} allowClear>
          <Option value="100+">100+</Option>
          <Option value="500+">500+</Option>
          <Option value="1000+">1000+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="分享数" style={{ width: '100%' }} allowClear>
          <Option value="10+">10+</Option>
          <Option value="50+">50+</Option>
          <Option value="100+">100+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="收藏数" style={{ width: '100%' }} allowClear>
          <Option value="50+">50+</Option>
          <Option value="100+">100+</Option>
          <Option value="500+">500+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="评论数" style={{ width: '100%' }} allowClear>
          <Option value="10+">10+</Option>
          <Option value="50+">50+</Option>
          <Option value="100+">100+</Option>
        </Select>
      </Col>
      <Col span={4}>
        <Select placeholder="预估阅读数" style={{ width: '100%' }} allowClear>
          <Option value="1000+">1000+</Option>
          <Option value="5000+">5000+</Option>
          <Option value="1w+">1w+</Option>
        </Select>
      </Col>
    </Row>
  );

  return (
    <div
      className="note-search-container"
      style={{
        padding: 24,
        boxSizing: 'border-box',
      }}
    >
      <Card>
        {/* 搜索标签页 */}
        <Tabs defaultActiveKey="1" style={{ marginBottom: 24 }}>
          <TabPane tab="笔记搜索" key="1">
            {/* 搜索区域 */}
            <div style={{ marginBottom: 24 }}>
              <Row gutter={16} align="middle">
                <Col span={16}>
                  <Search
                    placeholder="请输入关键词、内容关键词搜索"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    enterButton={
                      <Button type="primary" icon={<SearchOutlined />}>
                        搜索
                      </Button>
                    }
                    size="large"
                    onSearch={handleSearch}
                  />
                </Col>
                <Col span={8}>
                  <Button onClick={() => setFilterCollapsed(!filterCollapsed)}>
                    {filterCollapsed ? '展开筛选' : '收起筛选'}
                  </Button>
                </Col>
              </Row>
            </div>
            {/* 筛选条件 */}
            <div className="filter-collapse-container">
              <Collapse
                ghost
                activeKey={filterCollapsed ? [] : ['1']}
                style={{ background: 'transparent', border: 'none' }}
              >
                <Collapse.Panel
                  key="1"
                  header={null}
                  showArrow={false}
                  style={{ background: 'transparent', border: 'none' }}
                >
                  <div className="filter-section">
                    <div className="filter-title">基础筛选</div>
                    {filterOptions}
                  </div>

                  <div className="filter-section">
                    <div className="filter-title">作者属性</div>
                    {advancedFilterOptions}
                  </div>

                  <div className="filter-section">
                    <div className="filter-title">数据表现</div>
                    {dataOverviewOptions}
                  </div>
                </Collapse.Panel>
              </Collapse>
            </div>

            {/* 结果统计 */}
            <div className="result-summary">
              <Space>
                <Text>笔记数据</Text>
                <Text strong style={{ fontSize: 16 }}>
                  共<Text style={{ color: '#3A64FF' }}>{total}</Text>条结果
                </Text>
                <Text>清空筛选</Text>
                {/* <Button type="link" icon={<BarChartOutlined />}>
                  AI分析
                </Button> */}
              </Space>
            </div>

            {/* 数据表格 */}
            <div className="table-container">
              <Table
                columns={getColumns()}
                dataSource={dataSource}
                loading={loading}
                rowKey="id"
                sticky={{ offsetHeader: 0 }}
                onChange={(pagination, filters, sorter: any) => {
                  // 处理排序变化
                  if (sorter && sorter.columnKey) {
                    setCurrentSortColumn(sorter.columnKey);
                    // 更新搜索参数中的排序字段
                    const newParams = {
                      ...searchParams,
                      sort: sorter.columnKey,
                    };
                    setSearchParams(newParams);
                    // 重新获取数据
                    fetchNoteData(newParams, currentPage, pageSize);
                  } else {
                    setCurrentSortColumn('');
                  }
                }}
                pagination={{
                  current: currentPage,
                  total: total,
                  pageSize: pageSize,
                  showTotal: (total, range) =>
                    `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  onChange: handlePageChange,
                  onShowSizeChange: handlePageChange,
                }}
                scroll={{ x: 1200 }}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* 二维码弹窗 */}
      <Modal
        title="笔记详情二维码"
        open={qrModalVisible}
        onCancel={handleCloseQrModal}
        footer={null}
        centered
        width={400}
      >
        {selectedRecord && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>{selectedRecord.title}</Typography.Text>
            </div>
            <div style={{ marginBottom: 20 }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                  `xhsdiscover://item/${selectedRecord.id}?source=pcweb_access_limit`,
                )}`}
                alt="二维码"
                style={{ maxWidth: '200px', height: '200px' }}
              />
            </div>
            <Typography.Text type="secondary">
              小红书扫描二维码查看笔记详情
            </Typography.Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NoteSearch;
